import { API_BASE_URL } from "../config/api";

export type ARAgingMetrics = {
  currencyCode: string;
  currencySymbol: string;
  totalReceivables: number;
  overdueAmount: number;
  convertedTotalReceivables?: number | null;
  convertedOverdueAmount?: number | null;
  convertedCurrencyCode?: string | null;
  convertedCurrencySymbol?: string | null;
  oldestOutstanding: number;
  avgCollectionPeriod: number;
};

export type ARAgingBucket = {
  range: string;
  amount: number;
  percentage: number;
  color: string;
  currencyCode: string;
  currencySymbol: string;
};

export type ARAgingDistributionResponse = {
  asOfDate: string;
  totalOutstandingAmount: number;
  overdueAmount: number;
  notOverdueAmount: number;
  currencyCode: string;
  currencySymbol: string;
  buckets: ARAgingBucket[];
};

export type OverdueCustomer = {
  customerId: string;
  customer: string;
  currencyCode: string;
  currencySymbol: string;
  amount: number;
  invoiceCount: number;
  oldestAge: number;
  status: 'critical' | 'warning';
};

export type LatePayer = {
  customerId: string;
  customer: string;
  currencyCode: string;
  currencySymbol: string;
  lateFrequency: number;
  lateCount: number;
  totalCount: number;
  avgDelay: number;
  longestDelay: number;
  outstanding: number;
  trend: 'worsening' | 'stable' | 'improving';
};

export type ARInsight = {
  type: 'critical' | 'warning' | 'success' | 'brand';
  title: string;
  message: string;
  impact: 'high' | 'medium' | 'low';
};


function qs(params: Record<string, any>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '');
  if (!entries.length) return '';
  const search = new URLSearchParams(entries as any).toString();
  return `?${search}`;
}

// Helper to create headers with authentication
const createHeaders = (): HeadersInit => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  };
  
  // Add auth token if available
  const token = localStorage.getItem('token');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
};

export async function getMetrics(params: { companyId?: number; startDate?: string; endDate?: string } = {}): Promise<ARAgingMetrics[]> {
  const url = `${API_BASE_URL}/api/ar-aging/metrics${qs(params)}`;
  const res = await fetch(url, { headers: createHeaders() });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.message || errorData.error || `Failed to fetch AR aging metrics`);
  }
  return (await res.json()) as ARAgingMetrics[];
}

export async function getDistribution(
  params: { companyId?: number; startDate?: string; endDate?: string } = {},
): Promise<ARAgingDistributionResponse> {
  const url = `${API_BASE_URL}/api/ar-aging/distribution${qs(params)}`;
  const res = await fetch(url, { headers: createHeaders() });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.message || errorData.error || `Failed to fetch AR aging distribution`);
  }
  return (await res.json()) as ARAgingDistributionResponse;
}

export type PaginatedResponse<T> = {
  data: T[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
  };
};

export async function getOverdueCustomers(params: { companyId?: number; startDate?: string; endDate?: string; limit?: number; offset?: number } = {}): Promise<PaginatedResponse<OverdueCustomer>> {
  const url = `${API_BASE_URL}/api/ar-aging/overdue-customers${qs(params)}`;
  const res = await fetch(url, { headers: createHeaders() });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.message || errorData.error || `Failed to fetch overdue customers`);
  }
  return (await res.json()) as PaginatedResponse<OverdueCustomer>;
}

export async function getLatePayers(params: { companyId?: number; startDate?: string; endDate?: string } = {}): Promise<LatePayer[]> {
  const url = `${API_BASE_URL}/api/ar-aging/late-payers${qs(params)}`;
  const res = await fetch(url, { headers: createHeaders() });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.message || errorData.error || `Failed to fetch late payers`);
  }
  return (await res.json()) as LatePayer[];
}

export async function getInsights(params: { companyId?: number } = {}): Promise<ARInsight[]> {
  const url = `${API_BASE_URL}/api/ar-aging/insights${qs(params)}`;
  const res = await fetch(url, { headers: createHeaders() });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.message || errorData.error || `Failed to fetch AR aging insights`);
  }
  return (await res.json()) as ARInsight[];
}

